import { useState, useRef } from 'react'

/**
 * Find the matching minWidth mediaQuery in a list of queries and returns the associated value.
 *
 * eg: [[200, 'sm'], [300, 'md'], [400, 'lg']] -> if width === 325 -> returns 'md'
 */
function getMqValue(mqs: { minWidth: [number, string][] }, width: number) {
  let matchingIndex = mqs.minWidth.findIndex((item) => item[0] > width)
  if (matchingIndex === 0 && width < mqs.minWidth[0][0]) {
    return ''
  }
  matchingIndex = matchingIndex === -1 ? mqs.minWidth.length - 1 : matchingIndex - 1
  return mqs.minWidth[matchingIndex][1]
}

/**
 * Observes an element dimension and returns a matching query value when its size changes.
 */
export default function useElementSizeQuery(mediaQueries: {
  minWidth: [number, string][]
}): [{ minWidth: string }, (element: HTMLElement | null) => void] {
  const elementRef = useRef<HTMLElement | null>()
  const [media, setMedia] = useState({ minWidth: '' })
  const mediaRef = useRef({ minWidth: '' })

  const observer = useRef(
    new ResizeObserver((entries) => {
      const width = entries[0].contentBoxSize
        ? entries[0].contentBoxSize[0].inlineSize
        : entries[0].contentRect.width

      const newWidthValue = getMqValue(mediaQueries, width)
      if (newWidthValue !== mediaRef.current.minWidth) {
        const update = { minWidth: newWidthValue }
        mediaRef.current = update
        setMedia(update)
      }
    })
  )

  function setRef(ref: HTMLElement | null) {
    if (ref !== elementRef.current) {
      if (elementRef.current) {
        observer.current.unobserve(elementRef.current)
      }
    }
    if (ref) {
      observer.current.observe(ref)
    }
    elementRef.current = ref
  }

  return [media, setRef]
}
